import { useNavigate } from 'react-router-dom';
import { Screen } from '../../app/Frame';
import { useMeContext } from '../../app/MeContext';
import { useScreen, useToast } from '../../app/shell-context';
import { ApiError } from '../../api/client';
import { formatDayWithWeekday, formatTime } from '../../lib/time';
import { formatKop } from '../../lib/money';
import { plural } from '../../lib/plural';
import { Notice } from '../../ui/parts';
import { useBooking, useBookingGuard } from '../BookingContext';
import { useConfirmAppointment } from '../api';
import { HoldTimer } from '../HoldTimer';

/** Шаг 5 из 5: сверка деталей перед оплатой. */
export function Confirm() {
  const me = useMeContext();
  const navigate = useNavigate();
  const toast = useToast();
  const { patch } = useBooking();
  const booking = useBookingGuard((b) => b.appointmentId !== null && b.startsAt !== null);
  const confirm = useConfirmAppointment();
  const tz = me.center.tz;
  const tf = me.center.timeFormat;

  useScreen('Подтверждение', {
    // Оплата онлайн не подключена: платит семья на месте, а сотрудник отметит
    // оплату. Подтверждение снимает срок брони — с этого момента запись живёт.
    label: confirm.isPending
      ? 'Секунду…'
      : booking?.priceKop
        ? `Записаться · ${formatKop(booking.priceKop)} на месте`
        : 'Записаться',
    disabled: confirm.isPending,
    onClick: () => submit(),
  });

  if (!booking) return null;

  function submit() {
    if (!booking?.appointmentId) return;
    confirm.mutate(
      {
        id: booking.appointmentId,
        intake: {
          childName: booking.intake.name,
          age: booking.intake.age,
          note: booking.intake.note,
          phone: booking.intake.phone,
        },
      },
      {
        onSuccess: () => navigate('/done'),
        onError: (e) => {
          // Бронь могли снять, пока родитель читал сводку: возвращаем к выбору времени.
          if (e instanceof ApiError && e.is('BAD_STATE')) {
            patch({ appointmentId: null, holdExpiresAt: null, startsAt: null });
            toast('Бронь истекла — выберите время заново');
            navigate('/calendar', { replace: true });
            return;
          }
          toast(e instanceof ApiError ? e.message : 'Не получилось подтвердить запись');
        },
      },
    );
  }

  const address = booking.isOnline
    ? 'Видеосвязь, ссылка придёт в чат'
    : me.center.locations[0]?.address ?? 'адрес уточняется';

  const rows: [string, string][] = [
    ['Специалист', booking.specialistName],
    ['Формат', booking.serviceTitle || '—'],
    ['Когда', `${formatDayWithWeekday(booking.startsAt!, tz)} · ${formatTime(booking.startsAt!, tz, tf)}`],
    ['Длительность', booking.durationMin ? `${booking.durationMin} мин` : '—'],
    ['Ребёнок', childLine(booking.intake.name, booking.intake.age)],
    ['Где', address],
    ['Связь с вами', `${booking.intake.phone || me.client?.phone || '—'} · Telegram`],
  ];

  return (
    <Screen>
      <div>
        <p className="eyebrow">Шаг 5 из 5</p>
        {/* В прототипе здесь «после оплаты»: оплата на месте, слот закрепляется подтверждением. */}
        <p className="lead" style={{ marginTop: 6 }}>Проверьте детали — после подтверждения слот закрепится за вами.</p>
      </div>

      <HoldTimer />

      <dl className="summary">
        {rows.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
        <div className="total">
          <dt>К оплате</dt>
          <dd>{booking.priceKop === null ? '—' : formatKop(booking.priceKop)}</dd>
        </div>
      </dl>

      <Notice>
        {me.center.cancelHours
          ? `Отмена или перенос без штрафа — не позднее чем за ${me.center.cancelHours} ${plural(me.center.cancelHours, 'час', 'часа', 'часов')} до записи.`
          : 'Отменить или перенести можно в любой момент до записи.'}
      </Notice>

      <p className="footnote">Оплата на месте — администратор отметит её после визита</p>
    </Screen>
  );
}

function childLine(name: string, age: string): string {
  const n = name.trim() || '—';
  const a = age.trim();
  return a ? `${n}, ${a} ${plural(Number(a), 'год', 'года', 'лет')}` : n;
}
